import { useMemo } from 'react';
import { defaultLogger } from '@/common/infrastructure/services/LoggerService';
import { describeCatalogFailure } from '@/document-types/application/describeCatalogFailure';
import { useVariableCatalog } from '@/document-types/application/hooks/useVariableCatalog';
import { BASE_VARIABLE_CATALOG, mergeCatalogs } from '@/document-render/application/variableCatalog';
import { useHostConfig } from '@/host-config/presentation/HostConfigProvider';
import type { DocumentTypeRef, DocumentTypeVariableCategory } from '@/document-types/domain/types/DocumentType';

export type EditorVariablesState = {
  categories: DocumentTypeVariableCategory[];
  isLoading: boolean;
  error: unknown;
  isFallback: boolean;
};

// Many components consume this hook (and StrictMode renders twice), so the fallback is logged once per
// failed query result — keyed on the result's own error/data object — not once per consumer or render.
// A new failed result is a new object and logs again.
const loggedFallbackResults = new WeakSet<object>();

const warnFallbackOnce = (ref: DocumentTypeRef, data: unknown, error: unknown): void => {
  const resultKey = [error, data, ref].find((candidate): candidate is object => typeof candidate === 'object' && candidate !== null);
  if (!resultKey || loggedFallbackResults.has(resultKey)) return;
  loggedFallbackResults.add(resultKey);
  defaultLogger.warn('Using the fallback variable catalog: the API catalog was empty or failed to load.', {
    ref,
    ...(error ? describeCatalogFailure(error) : { message: 'Empty catalog' })
  });
};

export const useEditorVariables = (ref?: DocumentTypeRef): EditorVariablesState => {
  const { data, isLoading, error, isFetched } = useVariableCatalog(ref);
  const { variableCategories: hostCategories } = useHostConfig();

  return useMemo(() => {
    const hasCatalog = (data?.length ?? 0) > 0;
    const shouldUseFallback = !!ref && isFetched && (!hasCatalog || !!error);
    const catalog = shouldUseFallback ? BASE_VARIABLE_CATALOG : (data ?? []);
    // Host variables (e.g. CRM Data management fields) join the catalog once it has resolved, so a
    // picker never shows them alone while the document type's own variables are still loading.
    const hostVariables = hostCategories ?? [];
    const shouldAppendHost = !!ref && hostVariables.length > 0 && (shouldUseFallback || hasCatalog);
    const categories = shouldAppendHost ? mergeCatalogs(catalog, hostVariables) : catalog;

    if (shouldUseFallback) warnFallbackOnce(ref, data, error);

    return {
      categories,
      isLoading: !!ref && isLoading,
      error,
      isFallback: shouldUseFallback
    };
  }, [data, error, hostCategories, isFetched, isLoading, ref]);
};
